import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { Layout } from '../components/Layout';
import { ProductGridSkeleton } from '../components/States';

/**
 * Routes.
 *
 * Every route is code-split. The homepage, shop and product pages are what
 * most sessions touch; checkout, account and the policy pages are downloaded
 * only when someone goes there, which keeps the initial bundle to the shell
 * plus one route.
 */

const Home = lazy(() => import('../pages/Home'));
const Shop = lazy(() => import('../pages/Shop'));
const Collection = lazy(() => import('../pages/Collection'));
const Product = lazy(() => import('../pages/Product'));
const Search = lazy(() => import('../pages/Search'));
const Cart = lazy(() => import('../pages/Cart'));
const Checkout = lazy(() => import('../pages/Checkout'));
const OrderConfirmation = lazy(() => import('../pages/OrderConfirmation'));
const OrderTracking = lazy(() => import('../pages/OrderTracking'));
const Account = lazy(() => import('../pages/Account'));

const JournalIndex = lazy(() =>
  import('../pages/Journal').then((module) => ({ default: module.JournalIndex })),
);
const JournalEntry = lazy(() =>
  import('../pages/Journal').then((module) => ({ default: module.JournalEntry })),
);
const About = lazy(() => import('../pages/Static').then((m) => ({ default: m.About })));
const Shipping = lazy(() => import('../pages/Static').then((m) => ({ default: m.Shipping })));
const Returns = lazy(() => import('../pages/Static').then((m) => ({ default: m.Returns })));
const Privacy = lazy(() => import('../pages/Static').then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import('../pages/Static').then((m) => ({ default: m.Terms })));
const NotFound = lazy(() => import('../pages/Static').then((m) => ({ default: m.NotFound })));

/**
 * Route-level loading.
 *
 * A skeleton in the page's own shape rather than a spinner, so the layout is
 * already reserved when the chunk lands and nothing jumps.
 */
function RouteFallback() {
  return (
    <div className="container section">
      <p role="status" aria-live="polite" className="visually-hidden">
        Loading
      </p>
      <ProductGridSkeleton count={3} />
    </div>
  );
}

const page = (element: ReactNode) => <Suspense fallback={<RouteFallback />}>{element}</Suspense>;

/**
 * The admin panel.
 *
 * A sibling of the storefront rather than a child, because it has its own
 * shell: rendering the storefront header and footer around an operations
 * screen would be both wrong and slow. One lazy chunk, so a shopper never
 * downloads any of it.
 */
const AdminRoot = lazy(() => import('../admin/AdminRoot'));

const router = createBrowserRouter([
  {
    path: '/admin/*',
    element: (
      <Suspense fallback={null}>
        <AdminRoot />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <Layout />,
    children: [
      { index: true, element: page(<Home />) },
      { path: 'shop', element: page(<Shop />) },
      { path: 'collection/:slug', element: page(<Collection />) },
      { path: 'product/:slug', element: page(<Product />) },
      { path: 'search', element: page(<Search />) },
      { path: 'cart', element: page(<Cart />) },
      { path: 'checkout', element: page(<Checkout />) },
      // PayU's surl/furl land on the API, which redirects here.
      { path: 'checkout/processing', element: page(<OrderConfirmation />) },
      { path: 'order/confirmation', element: page(<OrderConfirmation />) },
      { path: 'track', element: page(<OrderTracking />) },
      { path: 'account', element: page(<Account />) },
      { path: 'journal', element: page(<JournalIndex />) },
      { path: 'journal/:slug', element: page(<JournalEntry />) },
      { path: 'about', element: page(<About />) },
      { path: 'shipping', element: page(<Shipping />) },
      { path: 'returns', element: page(<Returns />) },
      { path: 'privacy', element: page(<Privacy />) },
      { path: 'terms', element: page(<Terms />) },
      { path: '*', element: page(<NotFound />) },
    ],
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
